import { SectionType } from 'types/publishedAssets';
import { isHeroSection } from 'utils/heroSection';

export interface SectionTabGroup {
  /** The tab that owns every section in this group, or null when no tab does. */
  ownerSlug: string | null;
  sections: SectionType[];
}

/**
 * Gather consecutive sections into the tab that owns them.
 *
 * A tab's content is its titled section plus every untitled section absorbed after it, so the
 * tab — not the section — is the unit the viewport floor and the hidden flag belong to. Applying
 * either per section puts a whole viewport of empty space between a titled section and the
 * siblings that belong to the same tab (ENG-4526).
 *
 * The hero is never grouped: it sits outside the tab strip and stays visible whichever tab is
 * active, so folding it into a group would hide it along with that group.
 */
export function groupSectionsByTab(
  sections: readonly SectionType[],
  ownerSlugBySectionId: Record<string, string | null>,
): SectionTabGroup[] {
  return sections.reduce<SectionTabGroup[]>((groups, section) => {
    const ownerSlug = isHeroSection(section) ? null : (ownerSlugBySectionId[section.id] ?? null);
    const open = groups[groups.length - 1];
    // Only a run of sections sharing an owner merges. A section no tab claims keeps its own group,
    // because it renders under every tab rather than one, and the hero keeps its own so hiding a
    // tab cannot hide it.
    const continuesOpenGroup =
      open !== undefined &&
      ownerSlug !== null &&
      open.ownerSlug === ownerSlug &&
      !isHeroSection(section) &&
      !isHeroSection(open.sections[0]);

    if (continuesOpenGroup) {
      open.sections.push(section);
      return groups;
    }
    return [...groups, { ownerSlug, sections: [section] }];
  }, []);
}
